<template>
    <div>
        <vs-popup title="Banco de Dados de Integração"
            :button-close-hidden="false"
            :active.sync="isActive"
            @close="close()"
            id="create-integration-database-popup"
        >
            <vs-tabs>
                <vs-tab label="Provisionamento">
                    <p class="cidb-subtitle">
                        Provisiona o banco Postgres usado pela integração Alpha7 deste tenant. Um CNPJ já vinculado a outro tenant é bloqueado automaticamente.
                    </p>

                    <div v-if="!erpList.length" class="cidb-empty">
                        <feather-icon icon="DatabaseIcon" svgClasses="w-8 h-8" class="cidb-empty-icon" />
                        <p>Nenhum ERP Alpha7 cadastrado para este tenant.</p>
                    </div>

                    <div v-else class="cidb-list">
                        <div v-for="erp in erpList" :key="erp.id" class="cidb-row">
                            <div class="cidb-identity">
                                <span class="cidb-erp-name">{{ erp.corporate_name }}</span>
                                <span class="cidb-cnpj">{{ formatCnpj(erp.cnpj) }}</span>
                            </div>

                            <div class="cidb-action">
                                <transition name="cidb-fade" mode="out-in">
                                    <div v-if="rowState(erp).status === 'checking'" key="checking" class="cidb-status cidb-status--pending">
                                        <feather-icon icon="LoaderIcon" svgClasses="w-4 h-4" class="cidb-spin" />
                                        <span>Verificando…</span>
                                    </div>

                                    <vs-button
                                        v-else-if="rowState(erp).status === 'idle'"
                                        key="idle"
                                        color="primary"
                                        type="filled"
                                        icon-pack="feather"
                                        icon="icon-database"
                                        @click="startCreate(erp)"
                                    >
                                        Criar banco de dados
                                    </vs-button>

                                    <div v-else-if="rowState(erp).status === 'loading-suggest'" key="loading" class="cidb-status cidb-status--pending">
                                        <feather-icon icon="LoaderIcon" svgClasses="w-4 h-4" class="cidb-spin" />
                                        <span>Consultando nome sugerido…</span>
                                    </div>

                                    <div v-else-if="rowState(erp).status === 'editing'" key="editing" class="cidb-edit">
                                        <label class="cidb-label" :for="`cidb-input-${erp.id}`">Nome do banco de dados</label>
                                        <div class="cidb-edit-row">
                                            <input
                                                :id="`cidb-input-${erp.id}`"
                                                class="cidb-input"
                                                type="text"
                                                spellcheck="false"
                                                :value="rowState(erp).name"
                                                @input="setRowName(erp, $event.target.value)"
                                            />
                                            <vs-button
                                                :disabled="!rowState(erp).name"
                                                color="primary"
                                                type="filled"
                                                @click="confirmCreate(erp)"
                                            >
                                                Confirmar criação
                                            </vs-button>
                                        </div>
                                    </div>

                                    <div v-else-if="rowState(erp).status === 'creating'" key="creating" class="cidb-status cidb-status--pending">
                                        <feather-icon icon="LoaderIcon" svgClasses="w-4 h-4" class="cidb-spin" />
                                        <span>Criando <code class="cidb-pill">{{ rowState(erp).name }}</code>…</span>
                                    </div>

                                    <div v-else-if="rowState(erp).status === 'done'" key="done" class="cidb-status cidb-status--done">
                                        <feather-icon icon="CheckCircleIcon" svgClasses="w-4 h-4" />
                                        <span v-if="rowState(erp).justCreated">
                                            Banco <code class="cidb-pill cidb-pill--done">{{ rowState(erp).name }}</code> criado com sucesso
                                        </span>
                                        <span v-else>
                                            ERP vinculado ao banco <code class="cidb-pill cidb-pill--done">{{ rowState(erp).name }}</code>
                                        </span>
                                    </div>

                                    <div v-else-if="rowState(erp).status === 'error'" key="error" class="cidb-status cidb-status--error">
                                        <feather-icon icon="AlertTriangleIcon" svgClasses="w-4 h-4" />
                                        <span>{{ rowState(erp).errorMessage }}</span>
                                        <vs-button size="small" type="border" color="danger" @click="resetRow(erp)">Tentar de novo</vs-button>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>
                </vs-tab>

                <vs-tab label="Histórico">
                    <p class="cidb-subtitle">
                        Registro de auditoria de todas as criações de banco de integração feitas neste tenant.
                    </p>

                    <div v-if="logsLoading" class="cidb-empty">
                        <feather-icon icon="LoaderIcon" svgClasses="w-8 h-8 cidb-spin" class="cidb-empty-icon" />
                        <p>Carregando histórico…</p>
                    </div>

                    <div v-else-if="!logsList.length" class="cidb-empty">
                        <feather-icon icon="ClockIcon" svgClasses="w-8 h-8" class="cidb-empty-icon" />
                        <p>Nenhum registro de criação de banco de integração ainda.</p>
                    </div>

                    <div v-else class="cidb-list">
                        <div v-for="log in logsList" :key="log.id" class="cidb-log-row">
                            <div class="cidb-log-main">
                                <span class="cidb-log-date">{{ formatDate(log.created_at) }}</span>
                                <span class="cidb-log-user">{{ performedByLabel(log) }}</span>
                                <span class="cidb-cnpj">{{ formatCnpj(logCnpj(log)) }}</span>
                            </div>
                            <div class="cidb-log-details">
                                <code class="cidb-pill cidb-pill--done">{{ logDatabaseName(log) }}</code>
                                <span class="cidb-log-status">{{ logStatusLabel(log) }}</span>
                            </div>
                        </div>
                    </div>
                </vs-tab>
            </vs-tabs>
        </vs-popup>
    </div>
</template>

<script>
    import erpHttp from './../../http/requests/erps/index';
    import { mapGetters } from 'vuex';

    export default {
        data() {
            return {
                erpList: [],
                rowStates: {},
                logsList: [],
                logsLoading: false,
            }
        },
        props: {
            isActive: {
                type: Boolean,
                required: true
            },
            tenantName: {
                type: String,
                required: true
            }
        },
        emits: [
            'isActiveFalse'
        ],
        methods: {
            close() {
                this.$emit('isActiveFalse');
            },
            fetchErps() {
                erpHttp.getAllErps(this.tenantName, this.token)
                    .then(response => {
                        this.erpList = response.data.filter(erp => this.isAlpha7(erp));
                        this.rowStates = {};
                        this.erpList.forEach(erp => this.checkExistingDatabase(erp));
                    })
                    .catch(error => {
                        console.error('Erro ao buscar ERPs:', error);
                    });
            },
            isAlpha7(erp) {
                return String(erp.erp_name).toLowerCase() === 'alpha7' || erp.erp_id === 2;
            },
            formatCnpj(cnpj) {
                const digits = String(cnpj).replace(/\D/g, '');
                if (digits.length !== 14) {
                    return cnpj;
                }
                return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            },
            rowState(erp) {
                if (!this.rowStates[erp.id]) {
                    this.$set(this.rowStates, erp.id, { status: 'checking', name: '', alreadyExists: false, justCreated: false, errorMessage: '' });
                }
                return this.rowStates[erp.id];
            },
            checkExistingDatabase(erp) {
                const state = this.rowState(erp);
                state.status = 'checking';

                erpHttp.suggestIntegrationDatabaseName(this.tenantName, this.token, erp.id)
                    .then(response => {
                        const result = response.data;
                        state.name = result.name;
                        state.alreadyExists = !!result.alreadyExists;
                        state.status = result.alreadyExists ? 'done' : 'idle';
                    })
                    .catch(() => {
                        state.status = 'idle';
                    });
            },
            startCreate(erp) {
                const state = this.rowState(erp);
                state.status = 'loading-suggest';

                erpHttp.suggestIntegrationDatabaseName(this.tenantName, this.token, erp.id)
                    .then(response => {
                        const result = response.data;
                        state.name = result.name;

                        if (result.alreadyExists) {
                            state.alreadyExists = true;
                            state.status = 'done';
                            return;
                        }

                        state.status = 'editing';
                    })
                    .catch(error => {
                        state.status = 'error';
                        state.errorMessage = this.extractErrorMessage(error, 'Não foi possível consultar o nome do banco de integração.');
                    });
            },
            confirmCreate(erp) {
                const state = this.rowState(erp);
                state.status = 'creating';

                erpHttp.createIntegrationsDatabase(this.tenantName, this.token, erp.id, state.name, this.currentUser.email, this.currentUser.name)
                    .then(response => {
                        const result = response.data;
                        state.name = result.database;
                        state.alreadyExists = result.alreadyExists;
                        state.justCreated = true;
                        state.status = 'done';
                        this.fetchLogs();
                    })
                    .catch(error => {
                        state.status = 'error';
                        state.errorMessage = this.extractErrorMessage(error, 'Não foi possível criar o banco de integração. Tente novamente ou contate o suporte.');
                    });
            },
            extractErrorMessage(error, fallback) {
                const data = error.response ? error.response.data : null;

                if (typeof data === 'string') {
                    return data;
                }

                if (data && typeof data.message === 'string') {
                    return data.message;
                }

                return fallback;
            },
            setRowName(erp, value) {
                this.rowState(erp).name = value;
            },
            resetRow(erp) {
                this.$set(this.rowStates, erp.id, { status: 'idle', name: '', alreadyExists: false, justCreated: false, errorMessage: '' });
            },
            fetchLogs() {
                this.logsLoading = true;
                erpHttp.getIntegrationDatabaseLogs(this.tenantName, this.token)
                    .then(response => {
                        this.logsList = response.data;
                    })
                    .catch(error => {
                        console.error('Erro ao buscar histórico:', error);
                    })
                    .finally(() => {
                        this.logsLoading = false;
                    });
            },
            parseLogValues(log) {
                try {
                    return JSON.parse(log.new_values) || {};
                } catch (error) {
                    return {};
                }
            },
            performedByLabel(log) {
                const values = this.parseLogValues(log);
                const performedBy = values.performed_by || {};
                return performedBy.name || performedBy.email || log.user_name || 'Desconhecido';
            },
            logCnpj(log) {
                const values = this.parseLogValues(log);
                return (values.request && values.request.cnpj) || '';
            },
            logDatabaseName(log) {
                const values = this.parseLogValues(log);
                return (values.response && values.response.database) || '—';
            },
            logStatusLabel(log) {
                const values = this.parseLogValues(log);
                const response = values.response || {};
                return response.created ? 'Criado' : (response.alreadyExists ? 'Já existia' : '');
            },
            formatDate(dateString) {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return dateString;
                }
                return date.toLocaleString('pt-BR');
            },
        },
        watch: {
            isActive(value) {
                if (value) {
                    this.fetchErps();
                    this.fetchLogs();
                }
            },
        },
        computed: {
            ...mapGetters([
                'token'
            ]),
            currentUser() {
                return this.$store.state.AppActiveUser || {};
            },
        },
    }
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap');

#create-integration-database-popup {
    .vs-popup {
        width: 760px !important;
    }

    .vs-popup--content {
        padding-top: 0.25rem;
    }
}
</style>

<style lang="scss" scoped>
.cidb-subtitle {
    font-size: 0.85rem;
    line-height: 1.5;
    color: #6e6b7b;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #ebe9f1;
}

.cidb-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 3rem 1rem;
    color: #b9b9c3;

    p {
        font-size: 0.9rem;
        margin: 0;
    }
}

.cidb-empty-icon {
    color: #d8d6e1;
}

.cidb-list {
    display: flex;
    flex-direction: column;
}

.cidb-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1rem 0;
    border-bottom: 1px solid #ebe9f1;

    &:last-child {
        border-bottom: none;
    }
}

.cidb-identity {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
}

.cidb-erp-name {
    font-weight: 600;
    font-size: 0.95rem;
    color: #1e1e2d;
}

.cidb-cnpj {
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    font-size: 0.78rem;
    color: #6e6b7b;
    letter-spacing: 0.01em;
}

.cidb-action {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
}

.cidb-log-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 0.85rem 0;
    border-bottom: 1px solid #ebe9f1;

    &:last-child {
        border-bottom: none;
    }
}

.cidb-log-main {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
}

.cidb-log-date {
    font-size: 0.8rem;
    color: #b9b9c3;
}

.cidb-log-user {
    font-weight: 600;
    font-size: 0.9rem;
    color: #1e1e2d;
}

.cidb-log-details {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

.cidb-log-status {
    font-size: 0.8rem;
    color: #6e6b7b;
}

.cidb-status {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.85rem;
    white-space: nowrap;

    &--pending {
        color: #6e6b7b;
    }

    &--done {
        color: #28c76f;
    }

    &--error {
        color: #ea5455;
        white-space: normal;
    }
}

.cidb-pill {
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    font-size: 0.78rem;
    background: #f1f0fb;
    color: #5e50ee;
    border: 1px solid #e2defc;
    border-radius: 6px;
    padding: 0.1rem 0.45rem;

    &--done {
        background: #e9fbf1;
        color: #1f9d5c;
        border-color: #c7f2da;
    }
}

.cidb-edit {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.35rem;
}

.cidb-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #b9b9c3;
}

.cidb-edit-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

.cidb-input {
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    font-size: 0.85rem;
    padding: 0.5rem 0.7rem;
    border: 1px solid #d8d6e1;
    border-radius: 6px;
    min-width: 220px;
    color: #1e1e2d;
    background: #fff;
    transition: border-color 0.15s ease;

    &:focus {
        outline: none;
        border-color: #7367f0;
        box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
    }
}

.cidb-spin {
    animation: cidb-spin 0.9s linear infinite;
}

@keyframes cidb-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.cidb-fade-enter-active,
.cidb-fade-leave-active {
    transition: opacity 0.15s ease;
}

.cidb-fade-enter,
.cidb-fade-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .cidb-spin {
        animation: none;
    }

    .cidb-fade-enter-active,
    .cidb-fade-leave-active {
        transition: none;
    }
}
</style>
